import { FormEvent, useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import type { CreateSavedListRequest } from '../../types/savedLists';

interface SaveListModalProps {
  open: boolean;
  selectedMembers: any[];
  onClose: () => void;
  onSave: (payload: CreateSavedListRequest) => Promise<void>;
}

const LIST_NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

interface ValidationErrors {
  listName?: string;
  description?: string;
  members?: string;
}

export default function SaveListModal({ open, selectedMembers, onClose, onSave }: SaveListModalProps) {
  const [listName, setListName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setListName('');
      setDescription('');
      setErrors({});
      setIsSaving(false);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const validate = (): boolean => {
    const nextErrors: ValidationErrors = {};

    if (!listName.trim()) {
      nextErrors.listName = 'List Name is required.';
    } else if (listName.trim().length < 3) {
      nextErrors.listName = 'List Name must be at least 3 characters.';
    } else if (listName.trim().length > LIST_NAME_MAX_LENGTH) {
      nextErrors.listName = `List Name cannot be more than ${LIST_NAME_MAX_LENGTH} characters.`;
    }

    if (description.trim().length > DESCRIPTION_MAX_LENGTH) {
      nextErrors.description = `Description cannot be more than ${DESCRIPTION_MAX_LENGTH} characters.`;
    }

    if (selectedMembers.length === 0) {
      nextErrors.members = 'Select at least one member.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    setIsSaving(true);

    try {
      await onSave({
        listName: listName.trim(),
        description: description.trim(),
        memberUserIds: selectedMembers.map((member) => String(member.userId)),
        members: selectedMembers,
        createdByUserId: '',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="w-full max-w-2xl h-[80vh] flex flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Save Private List</h2>
            <p className="text-sm text-gray-500">Create a reusable talent pool for later messaging, lists, and exports.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex h-full flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-5">
              <div>
                <label htmlFor="listName" className="mb-2 block text-sm font-semibold text-gray-800">
                  List Name <span className="text-red-600">*</span>
                </label>
                <input
                  id="listName"
                  value={listName}
                  onChange={(event) => {
                    setListName(event.target.value);
                    setErrors((current) => ({ ...current, listName: undefined }));
                  }}
                  maxLength={LIST_NAME_MAX_LENGTH}
                  placeholder="Enter a name for your private list"
                  className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
                <div className="mt-2 flex items-start justify-between gap-3">
                  {errors.listName ? <p className="text-sm text-red-600">{errors.listName}</p> : <span />}
                  <span className="shrink-0 text-xs text-gray-500">
                    {listName.length}/{LIST_NAME_MAX_LENGTH}
                  </span>
                </div>
              </div>

              <div>
                <label htmlFor="description" className="mb-2 block text-sm font-semibold text-gray-800">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    setErrors((current) => ({ ...current, description: undefined }));
                  }}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  rows={4}
                  placeholder="Describe your private list"
                  className="w-full resize-none rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
                <div className="mt-2 flex items-start justify-between gap-3">
                  {errors.description ? <p className="text-sm text-red-600">{errors.description}</p> : <span />}
                  <span className="shrink-0 text-xs text-gray-500">
                    {description.length}/{DESCRIPTION_MAX_LENGTH}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between text-sm font-semibold text-gray-900">
                  <span>Selected Members</span>
                  <span>{selectedMembers.length}</span>
                </div>
                {errors.members ? <p className="mb-3 text-sm text-red-600">{errors.members}</p> : null}
                <div className="grid gap-2 max-h-[40vh] overflow-y-auto">
                  {selectedMembers.map((member) => (
                    <div key={member.userId} className="flex items-center gap-3 rounded-lg bg-white px-3 py-2 shadow-sm">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
                        {member.firstName?.[0] || member.fullName?.[0] || member.userName?.[0] || 'U'}
                      </span>
                      <div className="min-w-0 flex-1 text-sm">
                        <div className="truncate font-medium text-slate-900">
                          {member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : member.fullName || member.userName || 'Unknown User'}
                        </div>
                        <div className="truncate text-xs text-slate-500">
                          {member.itSpecialtyName || member.currentOrganization || member.email || 'Member'}
                        </div>
                      </div>
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 border-t border-gray-200 bg-white px-6 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="inline-flex h-11 items-center justify-center rounded-lg border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save List'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
